/**
 * What an untitled recap is called, wherever it is shown.
 *
 * A recap title is optional (share-your-day, decided with Kurtis 2026-09-23:
 * blank by default, like an Instagram post). Blank is stored as '' and
 * every surface that names the recap — the feed card, the album page, the
 * composer's "left blank, your post shows…" preview — renders THIS instead.
 * One function, so the preview can't promise a headline the card doesn't
 * show. It is rendered per viewer, in their language; the server never
 * stores a generated title, because that would freeze it in the author's.
 *
 *   one day, with places  →  "Saturday, Jun 14 · Antwerp Zoo → Park → Luigi's"
 *   one day, no places    →  "Saturday, Jun 14"
 *   several days          →  the trip's name, else the date range
 */
import i18n from '../i18n';
import { formatDateRangeShort, parseLocalDate } from './dateUtils';

const MAX_ROUTE_PLACES = 3;

function formatDayLong(iso) {
  const date = parseLocalDate(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(i18n.language || 'en', {
    weekday: 'long', month: 'short', day: 'numeric'
  });
}

/**
 * @param {object} args
 * @param {string[]} [args.dates]   'YYYY-MM-DD' days the recap covers
 * @param {string[]} [args.places]  place names in the day's order (dupes ok)
 * @param {string} [args.tripName]
 * @returns {string}
 */
export function recapFallbackTitle({ dates = [], places = [], tripName = null } = {}) {
  const days = [...new Set((dates || []).filter(Boolean).map(d => String(d).slice(0, 10)))].sort();

  if (days.length === 1) {
    const day = formatDayLong(days[0]);
    const names = [];
    for (const place of places || []) {
      const name = String(place || '').trim();
      if (name && !names.includes(name)) names.push(name);
    }
    if (names.length === 0) return day || tripName || i18n.t('labels:recapTitle.untitled');
    const shown = names.slice(0, MAX_ROUTE_PLACES).join(' → ');
    const extra = names.length - MAX_ROUTE_PLACES;
    const route = extra > 0
      ? i18n.t('labels:recapTitle.routeMore', { route: shown, count: extra })
      : shown;
    return day ? i18n.t('labels:recapTitle.dayWithRoute', { day, route }) : route;
  }

  if (tripName) return tripName;
  if (days.length > 1) return formatDateRangeShort(days[0], days[days.length - 1]);
  return i18n.t('labels:recapTitle.untitled');
}

/**
 * What an album CARD is called: its own title, else the recap's day and
 * places, else its trip. `typeData` is normalizeCardData's album typeData.
 * Shared by the card body and the card's accessible label, so a screen
 * reader hears the same name the eye reads.
 */
export function albumCardTitle(title, typeData = {}) {
  if (title) return title;
  const { recapDates, headlinePlaces, tripName } = typeData || {};
  return recapFallbackTitle({
    dates: recapDates || [],
    places: headlinePlaces || [],
    tripName: tripName || null
  });
}

export default recapFallbackTitle;
